import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import { useSearchHubspotCompanies } from '@/pages/admin/api/queries/useAccounts';
import { CompanyHitLine, CrmSearch } from './CrmSearch';

interface AssignCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account being assigned — its own hit is not a conflict. */
  accountId: string;
  onAssign: (companyId: string) => void;
  isAssigning?: boolean;
}

/**
 * Picks the HubSpot company an account IS.
 *
 * Searches by name or domain and assigns by id. That split is not cosmetic: a
 * name-based lookup once sent an invoice to an unrelated company, so what a
 * person recognises and what gets stored are deliberately different things.
 *
 * Hits another account already holds are shown and refused rather than hidden —
 * seeing the conflict is what prevents it, and hiding taken companies would
 * make a mistyped search look like a company that does not exist.
 */
export function AssignCompanyDialog({
  open,
  onOpenChange,
  accountId,
  onAssign,
  isAssigning
}: AssignCompanyDialogProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const { data, isLoading } = useSearchHubspotCompanies(term);
  const hits = data?.companies ?? [];
  const takenByOther = (hit: (typeof hits)[number]) =>
    hit.linkedAccount !== null && hit.linkedAccount._id !== accountId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('accountAdmin.assignCompany')}</DialogTitle>
          <DialogDescription>{t('accountAdmin.assignCompanyHint')}</DialogDescription>
        </DialogHeader>

        <CrmSearch
          autoFocus
          term={term}
          onTermChange={setTerm}
          placeholder={t('accountAdmin.searchCompanyPlaceholder')}
          isLoading={isLoading}
          hits={hits}
          keyOf={(hit) => hit.companyId}
          busy={isAssigning}
          // The account's own company is not a conflict — only somebody else's.
          isDisabled={(hit) => takenByOther(hit)}
          renderHit={(hit) => (
            <CompanyHitLine
              hit={hit}
              badge={
                takenByOther(hit) && (
                  <Badge variant="outline" className="shrink-0">
                    <Link2 className="w-3 h-3 mr-1" />
                    {t('accountAdmin.companyTaken')}
                  </Badge>
                )
              }
            />
          )}
          onPick={(hit) => onAssign(hit.companyId)}
          minText={t('accountAdmin.searchCompanyMin')}
          emptyText={t('accountAdmin.searchCompanyEmpty')}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
